/**
 * Reads what an uploaded Fragebogen 2 document already knows about itself.
 *
 * The documents share one template: it prints its own progress into #ppct
 * ("46%") and its send button calls sendMail(). A document built differently
 * simply yields no progress and no send, and opening is still tracked.
 */

export function readProgress(doc) {
  const text = doc?.getElementById?.('ppct')?.textContent || '';
  const match = /(\d{1,3})\s*%/.exec(text);
  return match ? Math.min(100, Number(match[1])) : null;
}

/** The send button stays disabled until everything is answered, so a click means "abgeschickt". */
export const SUBMIT_SELECTOR = '[onclick*="sendMail"]';

/** The questionnaire's own submit button, or null in a document shaped differently. */
export function findSubmitButton(doc) {
  return doc?.querySelector?.(SUBMIT_SELECTOR) || null;
}

export function isSubmitClick(target) {
  return Boolean(target?.closest?.(SUBMIT_SELECTOR));
}

/**
 * Merges tracking updates and writes at most once per interval, always with the
 * newest values, so typing through a questionnaire does not become a request
 * per keystroke.
 */
export function createThrottledWriter(write, intervalMs) {
  let pending = null;
  let lastSent = 0;
  let timer = null;

  function send(options) {
    clearTimeout(timer);
    timer = null;
    if (!pending) return;
    const patch = pending;
    pending = null;
    lastSent = Date.now();
    // Handed back so an immediate write can be waited for — the submit needs to
    // know whether the answers really landed before it tells the candidate so.
    return write(patch, options);
  }

  return {
    push(patch, { immediate = false } = {}) {
      pending = { ...pending, ...patch };
      if (immediate) return send();
      if (timer) return undefined;
      timer = setTimeout(send, Math.max(0, lastSent + intervalMs - Date.now()));
    },
    /** Sends what is pending now; options reach the write, e.g. { keepalive: true } on page exit. */
    flush(options) {
      return send(options);
    },
  };
}

/* ---------------------------------------------------------------------------
 * Reading the answers out of a finished questionnaire.
 *
 * Nothing here asks the document to send anything: it is loaded into our own
 * page (srcdoc, same origin), so the answers are simply there to be read. The
 * documents stay untouched — which is the point, since they are handed over as
 * files and nobody wants to re-edit 27 of them.
 *
 * What the template marks, and what we therefore read:
 *   [data-pdf]        the question's export label — the only stable name a
 *                     question has (the data-id keys are positional and shift
 *                     as soon as a question is inserted, see the template's
 *                     runtime id assignment)
 *   .copt.chosen      the picked single-choice option
 *   .mopt.on          the picked multi options; [data-real] marks the ones the
 *                     template counts as genuine on a [data-grade] question
 *   [data-correct]    the right option of a puzzle; a wrong pick is marked
 *                     .pz-opt.wrong
 *   .drop.on          how far a 1–10 rating row is filled, labelled by .rlabel
 *   [data-answer]     a free-text answer, blank when it still holds its starter
 * ------------------------------------------------------------------------- */

const text = (node) => (node?.textContent || '').replace(/\s+/g, ' ').trim();

function readChoice(el) {
  const chosen = el.querySelector('.copt.chosen');
  return chosen ? { answer: text(chosen) } : { answer: null };
}

function readMulti(el) {
  const picked = Array.from(el.querySelectorAll('.mopt.on'));
  if (!picked.length) return { answer: null };
  const graded = el.hasAttribute('data-grade');
  const options = picked.map((option) => ({
    text: text(option),
    ...(graded ? { genuine: option.hasAttribute('data-real') } : {}),
  }));
  return {
    answer: options.map((option) => option.text),
    options,
    ...(graded
      ? {
          genuineCount: options.filter((option) => option.genuine).length,
          decoyCount: options.filter((option) => !option.genuine).length,
        }
      : {}),
  };
}

function readPuzzle(el) {
  const right = el.querySelector(`.pz-opt[data-opt="${el.getAttribute('data-correct')}"]`);
  const wrong = el.querySelector('.pz-opt.wrong');
  // The template reveals the right option on every answered puzzle, so a
  // marked wrong pick is the only sign the candidate missed it.
  const answered = el.classList.contains('answered');
  if (!answered) return { answer: null };
  return {
    answer: wrong ? text(wrong) : text(right),
    correct: !wrong,
    expected: text(right),
  };
}

function readText(el) {
  const field = el.querySelector('[data-answer]');
  const value = (field?.value || '').trim();
  const starter = (field?.getAttribute('data-starter') || '').trim();
  return { answer: value && value !== starter && value !== '…' ? value : null };
}

function questionType(el) {
  if (el.hasAttribute('data-choice')) return 'choice';
  if (el.hasAttribute('data-multi')) return 'multi';
  if (el.hasAttribute('data-puzzle')) return 'puzzle';
  return 'text';
}

const READERS = { choice: readChoice, multi: readMulti, puzzle: readPuzzle, text: readText };

/** The 1–10 self-assessment rows, which carry their label in .rlabel instead of data-pdf. */
function readRatings(scope) {
  return Array.from(scope.querySelectorAll('[data-rate]')).map((row) => {
    const value = row.querySelectorAll('.drop.on').length;
    return {
      label: text(row.querySelector('.rlabel')),
      type: 'rating',
      answer: value || null,
      value: value || null,
      max: row.querySelectorAll('.drop').length || 10,
    };
  });
}

/**
 * What a document has to carry for us to read anything out of it — checked
 * before it is stored, so a file that nobody can evaluate never becomes the
 * questionnaire of a job.
 *
 * All 29 handed-over questionnaires come out of one template and carry exactly
 * the same markers; the only thing that differs is how many options a question
 * offers. This is what keeps it that way. Counting happens over the parsed DOM
 * rather than the file's text, because the markup is not written uniformly
 * enough for that (`class="module card"` vs `class="module"`), and a text
 * search would therefore report numbers that are simply wrong.
 *
 * Note where this check can and cannot sit: PocketBase stores the file as a
 * file and never looks inside, so this guards the upload, not the collection.
 * It stops the accident, not a determined bypass.
 */
export function inspectQuestionnaire(doc) {
  const all = (selector) => Array.from(doc?.querySelectorAll?.(selector) || []);
  const questions = all('[data-pdf]');
  const found = {
    questions: questions.length,
    ratings: all('[data-rate]').length,
    modules: all('section.module').length,
    choice: questions.filter((el) => questionType(el) === 'choice').length,
    multi: questions.filter((el) => questionType(el) === 'multi').length,
    puzzle: questions.filter((el) => questionType(el) === 'puzzle').length,
    text: questions.filter((el) => questionType(el) === 'text').length,
    graded: all('[data-grade]').length,
    submit: Boolean(findSubmitButton(doc)),
    progress: Boolean(doc?.getElementById?.('ppct')),
  };

  const problems = [];
  if (!found.submit) {
    problems.push({
      level: 'blocker',
      text: 'Kein Absende-Knopf gefunden. Ohne ihn merken wir nicht, wann jemand fertig ist — die Antworten kämen nie bei uns an.',
    });
  }
  if (!found.questions && !found.ratings) {
    problems.push({
      level: 'blocker',
      text: 'Keine auswertbaren Fragen gefunden. Entweder ist das kein Fragebogen 2, oder er ist anders gebaut als die bisherigen.',
    });
  }
  if (found.submit && found.questions && !found.progress) {
    problems.push({
      level: 'warning',
      text: 'Keine Fortschrittsanzeige gefunden. Die Antworten kommen an, aber "in Arbeit · 60 %" bleibt leer.',
    });
  }
  if (found.questions && !found.modules) {
    problems.push({
      level: 'warning',
      text: 'Keine Abschnitte gefunden. Die Antworten kommen an, lassen sich aber keinem Modul zuordnen.',
    });
  }

  return { ...found, problems, ok: !problems.some((problem) => problem.level === 'blocker') };
}

/**
 * Every answer of a questionnaire, in the order the candidate saw them —
 * ratings first per module, like the document's own mail summary.
 */
export function readAnswers(doc) {
  const root = doc?.body;
  if (!root) return [];

  const sections = Array.from(root.querySelectorAll('section.module'));
  const scopes = sections.length ? sections : [root];

  return scopes.flatMap((scope) => {
    const module = scope.dataset?.mod || null;
    const withModule = (entry) => (module ? { ...entry, module } : entry);

    const ratings = readRatings(scope).map(withModule);
    const questions = Array.from(scope.querySelectorAll('[data-pdf]')).map((el) => {
      const type = questionType(el);
      return withModule({ label: el.getAttribute('data-pdf'), type, ...READERS[type](el) });
    });

    return [...ratings, ...questions];
  });
}

/**
 * The verdict the document computes for itself: how much is filled in, whether
 * the puzzle was solved, the rating average. This is what the Ergebnisse view
 * shows as "Punkte" — Fragebogen 2 has no weighted score of its own.
 */
export function readResult(doc) {
  const answers = readAnswers(doc);
  const answered = answers.filter((entry) => entry.answer !== null && entry.answer !== undefined);
  const puzzles = answers.filter((entry) => entry.type === 'puzzle' && entry.answer !== null);
  const ratings = answers.filter((entry) => entry.type === 'rating' && entry.value);
  const graded = answers.filter((entry) => typeof entry.genuineCount === 'number');

  return {
    progress: readProgress(doc),
    answeredCount: answered.length,
    questionCount: answers.length,
    puzzlesSolved: puzzles.filter((entry) => entry.correct).length,
    puzzleCount: puzzles.length,
    ratingAverage: ratings.length
      ? Math.round((ratings.reduce((sum, entry) => sum + entry.value, 0) / ratings.length) * 10) / 10
      : null,
    genuineCount: graded.reduce((sum, entry) => sum + entry.genuineCount, 0),
    decoyCount: graded.reduce((sum, entry) => sum + entry.decoyCount, 0),
  };
}
